import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { 
  fetchPublicOrganizationById, 
  fetchPublicOrganizationVendors,
  fetchPublicOrganizationEvents
} from '../apiClient';
import { FaTh, FaList, FaBuilding, FaChevronLeft, FaChevronRight } from 'react-icons/fa';
import { MdLocationOn } from 'react-icons/md';
import Layout from '../components/Layout';

function OrganizationPage() {
  const [organization, setOrganization] = useState(null);
  const [vendors, setVendors] = useState([]);
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isListView, setIsListView] = useState(false);
  const [activeTab, setActiveTab] = useState('vendors');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 6;
  const { id } = useParams();

  const activeItems = activeTab === 'vendors' ? vendors : events;
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = activeItems.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(activeItems.length / itemsPerPage);

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab, isListView]);

  const Pagination = () => (
    <div className="flex justify-center items-center space-x-4 mt-8">
      <button
        onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
        disabled={currentPage === 1}
        className={`p-2 rounded-full transition-colors duration-200 ${
          currentPage === 1
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Previous page"
      >
        <FaChevronLeft className="w-5 h-5" />
      </button>
      
      <div className="flex items-center space-x-2">
        {[...Array(totalPages)].map((_, index) => (
          <button
            key={index + 1}
            onClick={() => setCurrentPage(index + 1)}
            className={`w-8 h-8 rounded-full ${
              currentPage === index + 1
                ? 'bg-blue-600 text-white'
                : 'text-gray-700 hover:bg-gray-100'
            }`}
          >
            {index + 1}
          </button>
        ))}
      </div>

      <button
        onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
        disabled={currentPage === totalPages || totalPages === 0}
        className={`p-2 rounded-full transition-colors duration-200 ${
          currentPage === totalPages || totalPages === 0
            ? 'text-gray-300 cursor-not-allowed'
            : 'text-gray-600 hover:bg-gray-100'
        }`}
        aria-label="Next page"
      >
        <FaChevronRight className="w-5 h-5" />
      </button>
    </div>
  );

  useEffect(() => {
    const loadData = async () => {
      try {
        // Fetch organization data
        const orgData = await fetchPublicOrganizationById(id);
        if (orgData.error) throw orgData.error;
        setOrganization(orgData.data);

        const vendorsData = await fetchPublicOrganizationVendors(id);
        if (vendorsData.error) throw vendorsData.error;
        setVendors(vendorsData.data || []);

  
        const eventsData = await fetchPublicOrganizationEvents(id);
        if (eventsData.error) throw eventsData.error;
        setEvents(eventsData.data || []);
      } catch (error) {
        console.error('Error loading organization page:', error);
        setError(error.message);
      } finally {
        setLoading(false);
      }
    };

    if (id) {
      loadData();
    }
  }, [id]);

  const renderVendors = () => {
    if (vendors.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <FaBuilding className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Vendors Found</h3>
          <p className="text-gray-600">This organization doesn't have any vendors yet.</p>
        </div>
      );
    }

    return (
      <div className={`grid ${isListView ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
        {currentItems.map((vendor) => (
          <Link to={`/vendor-page/${vendor.id}`} key={vendor.id} className="block">
            <div className={`bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200 ${
              isListView ? 'p-4 flex' : 'relative'
            }`}>
              {isListView ? (
                <div className="flex items-center w-full">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                    {vendor.logoURL ? (
                      <img src={vendor.logoURL} alt={`${vendor.company_name} logo`} className="w-full h-full object-cover" />
                    ) : (
                      <FaBuilding className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">{vendor.company_name}</h3>
                    <p className="text-gray-600">{vendor.mascotname}</p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <MdLocationOn className="mr-1" />
                      <span>{vendor.address || 'No address provided'}</span>
                    </div>
                  </div>
                  <span className="text-blue-600 font-medium">View Vendor →</span>
                </div>
              ) : (
                <>
                  <div className="h-40 bg-gray-100 flex items-center justify-center">
                    {vendor.logoURL ? (
                      <img 
                        src={vendor.logoURL} 
                        alt={`${vendor.company_name} logo`} 
                        className="h-full w-full object-cover" 
                      />
                    ) : (
                      <div
                        className="w-full h-full flex items-center justify-center"
                        style={{
                          background: vendor.primary_color || '#1f3a8a',
                          color: '#fff'
                        }}
                      >
                        <span className="text-2xl font-bold">{vendor.short_name || vendor.company_name.substring(0, 2).toUpperCase()}</span>
                      </div>
                    )}
                  </div>
                  <div className="p-4">
                    <h3 className="text-lg font-bold truncate">{vendor.company_name}</h3>
                    <p className="text-gray-600 truncate">{vendor.mascotname}</p>
                    <div className="flex items-center text-gray-500 text-sm my-2">
                      <MdLocationOn className="mr-1 flex-shrink-0" />
                      <span className="truncate">{vendor.address || 'No address provided'}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t text-blue-600 font-medium">
                      View Vendor →
                    </div>
                  </div>
                </>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  const renderEvents = () => {
    if (events.length === 0) {
      return (
        <div className="text-center py-12">
          <div className="bg-gray-100 rounded-full p-4 w-16 h-16 mx-auto mb-4 flex items-center justify-center">
            <FaBuilding className="w-8 h-8 text-gray-400" />
          </div>
          <h3 className="text-lg font-semibold text-gray-800 mb-2">No Upcoming Events</h3>
          <p className="text-gray-600">Check back later for new events from this organization.</p>
        </div>
      );
    }

    const defaultEventImageUrl = "https://www.pncfairfaxconnection.com/wp-content/themes/pnc-child/img/event_placeholder.jpg";

    return (
      <div className={`grid ${isListView ? 'grid-cols-1' : 'grid-cols-1 md:grid-cols-2 lg:grid-cols-3'} gap-6`}>
        {currentItems.map((event) => (
          <Link to={`/event-page/${event.id}`} key={event.id} className="block">
            <div className={`bg-white shadow-lg rounded-lg overflow-hidden border border-gray-200 ${
              isListView ? 'p-4 flex' : 'relative'
            }`}>
              {isListView ? (
                <div className="flex items-center w-full">
                  <div className="w-16 h-16 bg-gray-100 rounded-full flex items-center justify-center mr-4 overflow-hidden">
                    <img 
                      src={event.default_image?.image_path || defaultEventImageUrl} 
                      alt={event.title} 
                      className="w-full h-full object-cover" 
                    />
                  </div>
                  <div className="flex-1">
                    <h3 className="text-lg font-bold">{event.title}</h3>
                    <p className="text-gray-600">{event.event_type}</p>
                    <div className="flex items-center text-gray-500 text-sm">
                      <MdLocationOn className="mr-1" />
                      <span>{event.location || 'No location provided'}</span>
                    </div>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="font-semibold">
                      {new Date(event.start_date).toLocaleDateString()}
                    </span>
                    <span>
                      {new Date(event.start_date).toLocaleTimeString(undefined, {
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </span>
                    <span className="text-blue-600 font-medium">Details →</span>
                  </div>
                </div>
              ) : (
                <>
                  <div className="h-40 bg-gray-100">
                    <img 
                      src={event.default_image?.image_path || defaultEventImageUrl} 
                      alt={event.title} 
                      className="h-full w-full object-cover" 
                    />
                  </div>
                  <div className="p-4">
                    <div className="flex justify-between items-start">
                      <div>
                        <h3 className="text-lg font-bold truncate">{event.title}</h3>
                        <p className="text-gray-600 truncate">{event.event_type}</p>
                      </div>
                      <div className="text-right">
                        <div className="text-sm font-semibold">
                          {new Date(event.start_date).toLocaleDateString()}
                        </div>
                        <div className="text-sm">
                          {new Date(event.start_date).toLocaleTimeString(undefined, {
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center text-gray-500 text-sm my-2">
                      <MdLocationOn className="mr-1 flex-shrink-0" />
                      <span className="truncate">{event.location || 'No location provided'}</span>
                    </div>
                    <div className="mt-3 pt-3 border-t text-blue-600 font-medium">
                      View Event Details →
                    </div>
                  </div>
                </>
              )}
            </div>
          </Link>
        ))}
      </div>
    );
  };

  if (loading) return <div className="min-h-screen flex items-center justify-center"><p>Loading...</p></div>;
  if (error) return <div className="min-h-screen flex items-center justify-center"><p>Error: {error}</p></div>;
  if (!organization) return <div className="min-h-screen flex items-center justify-center"><p>Organization not found</p></div>;

  return (
    <Layout>
      <div className="min-h-screen bg-gray-100">
        <section
          className="text-center text-white py-28"
          style={{
            background: organization.primary_color || '#1f3a8a',
          }}
        >
          <div className="container mx-auto px-4">
            <h1 className="text-4xl font-bold mb-4">{organization.company_name}</h1>
            {organization.description && (
              <p className="max-w-3xl mx-auto opacity-90">{organization.description}</p>
            )}
          </div>
        </section>

        <main className="flex justify-center items-center py-8">
          <div className="w-full max-w-6xl bg-white p-6">
            <div className="flex flex-col sm:flex-row sm:items-center justify-between mb-6">
              <h2 className="text-3xl font-bold mb-4 sm:mb-0">{organization.company_name}</h2>
              <div className="flex bg-gray-100 rounded-lg p-1 self-start">
                <button
                  onClick={() => setActiveTab('vendors')}
                  className={`px-3 py-2 md:px-4 text-sm md:text-base rounded-md transition-colors duration-200 ${
                    activeTab === 'vendors'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Vendors
                </button>
                <button
                  onClick={() => setActiveTab('events')}
                  className={`px-3 py-2 md:px-4 text-sm md:text-base rounded-md transition-colors duration-200 ${
                    activeTab === 'events'
                      ? 'bg-white text-blue-600 shadow-sm'
                      : 'text-gray-600 hover:text-gray-800'
                  }`}
                >
                  Upcoming Events
                </button>
              </div>
            </div>

            {activeItems.length > 0 && (
              <div className="flex justify-end mb-4">
                <button
                  className={`p-2 ${!isListView ? 'text-white' : 'text-gray-600'}`}
                  style={{ backgroundColor: !isListView ? organization.primary_color || '#1f3a8a' : 'transparent' }}
                  onClick={() => setIsListView(false)}
                >
                  <FaTh />
                </button>
                <button
                  className={`p-2 ${isListView ? 'text-white' : 'text-gray-600'}`}
                  style={{ backgroundColor: isListView ? organization.primary_color || '#1f3a8a' : 'transparent' }}
                  onClick={() => setIsListView(true)}
                >
                  <FaList />
                </button>
              </div>
            )}

            {activeTab === 'vendors' ? renderVendors() : renderEvents()}

            {activeItems.length > itemsPerPage && <Pagination />}
          </div>
        </main>
      </div>
    </Layout>
  );
}

export default OrganizationPage; 